import { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { API_URL } from "../../Constants";
import axios from "axios";

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState(null);

  const fetchWhatsappNumber = async (companyId, token) => {
    try {
      const response = await axios.get(`${API_URL}/api/whatsappConfig/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.data && response.data.data && response.data.data.whatsappNumber) {
        setWhatsappNumber(response.data.data.whatsappNumber);
      }
    } catch (error) {
      console.error("Failed to fetch WhatsApp number:", error);
      // Don't throw error as this shouldn't block the login process
    }
  };

  const login = (token) => {
    localStorage.setItem("token", token);
    const decoded = decodeAndValidateToken(token);
    if (decoded) {
      setUser(decoded);
      setIsAuthenticated(true);

      const companyId = decoded.accountType === "COMPANY" ? decoded.id : decoded.companyId;

      // Fetch WhatsApp number after successful login
      fetchWhatsappNumber(companyId, token);

      // Call the session logging API with token in headers
      axios.post(
        `${API_URL}/api/session/login/${companyId}/${decoded.id}/${encodeURIComponent(decoded.name)}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      ).catch(error => {
        console.error("Session logging failed:", error);
        // Continue with login even if session logging fails
      });
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setIsAuthenticated(false);
    setWhatsappNumber(null); // Clear WhatsApp number on logout
  };

  const decodeAndValidateToken = (token) => {
    try {
      const decoded = jwtDecode(token);
      // Check token expiration
      if (decoded.exp * 1000 < Date.now()) {
        logout();
        return null;
      }
      return {
        id: decoded.id,
        name: `${decoded.fname} ${decoded.lname}`,
        email: decoded.sub,
        accountType: decoded.accountType,
        companyName: decoded.companyName,
        companyId: decoded.companyId,
        accountCreatedBy: decoded.accountCreatedBy,
        resellerId: decoded.resellerId,
        permissions: decoded.permissions,
      };
    } catch (error) {
      console.error("Token validation failed:", error);
      logout();
      return null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const userData = decodeAndValidateToken(token);
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        
        // Fetch WhatsApp number on app initialization if user is authenticated
        const companyId = userData.accountType === "COMPANY" ? userData.id : userData.companyId;
        fetchWhatsappNumber(companyId, token);
      }
    }
    setLoading(false); // Mark loading as complete
  }, []);

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        isAuthenticated, 
        loading,
        whatsappNumber, // Include WhatsApp number in context
        setWhatsappNumber, // Allow updating the WhatsApp number
        login, 
        logout 
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;